import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const PAGE_ROUTES = [
  '/',
  '/cinema',
  '/tvweb',
  '/musique',
  '/studio',
  '/tournage',
  '/bio',
  '/photos',
  '/actualites',
  '/contact',
];

const SWIPE_THRESHOLD = 70;
const VERTICAL_TOLERANCE = 1.2;
const MAX_SWIPE_DURATION = 900;
const TRACKPAD_THRESHOLD = 320;
const TRACKPAD_RESET_MS = 140;
const NAVIGATION_COOLDOWN_MS = 1200;

const isInteractiveTarget = (target) => {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      [
        '[data-page-swipe-lock="true"]',
        'button',
        'a',
        'input',
        'textarea',
        'select',
        'iframe',
        'video',
        'audio',
        '[role="button"]',
        '[contenteditable="true"]',
      ].join(',')
    )
  );
};

const SwipeNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const touchRef = useRef(null);
  const navigatingRef = useRef(false);
  const wheelXRef = useRef(0);
  const wheelResetRef = useRef(null);
  const cooldownRef = useRef(null);

  const navigateByDirection = (direction) => {
    if (navigatingRef.current) return;

    const currentIndex = PAGE_ROUTES.indexOf(location.pathname);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= PAGE_ROUTES.length) return;

    navigatingRef.current = true;
    navigate(PAGE_ROUTES[nextIndex], {
      state: { pageSwipeDirection: direction },
    });

    window.clearTimeout(cooldownRef.current);
    cooldownRef.current = window.setTimeout(() => {
      navigatingRef.current = false;
    }, NAVIGATION_COOLDOWN_MS);
  };

  useEffect(() => {
    navigatingRef.current = false;
    wheelXRef.current = 0;
  }, [location.pathname]);

  useEffect(() => {
    const onTouchStart = (event) => {
      if (event.touches.length !== 1 || isInteractiveTarget(event.target)) {
        touchRef.current = null;
        return;
      }

      const touch = event.touches[0];
      touchRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
        target: event.target,
      };
    };

    const onTouchEnd = (event) => {
      const start = touchRef.current;
      touchRef.current = null;

      if (!start || navigatingRef.current || event.changedTouches.length !== 1) return;
      if (isInteractiveTarget(start.target) || isInteractiveTarget(event.target)) return;

      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const duration = Date.now() - start.time;

      if (
        duration > MAX_SWIPE_DURATION ||
        Math.abs(dx) < SWIPE_THRESHOLD ||
        Math.abs(dx) < Math.abs(dy) * VERTICAL_TOLERANCE
      ) {
        return;
      }

      navigateByDirection(dx < 0 ? 1 : -1);
    };

    // Trackpad : les gestes horizontaux remontent sous forme de wheel + deltaX.
    const onWheel = (event) => {
      if (isInteractiveTarget(event.target) || navigatingRef.current) return;

      const horizontal = Math.abs(event.deltaX);
      const vertical = Math.abs(event.deltaY);

      // Trackpad volontaire uniquement : ignore les petits mouvements et
      // les gestes diagonaux/verticaux. Cela évite les changements de page
      // accidentels lors d'un simple scroll.
      if (horizontal < 8 || horizontal <= vertical * 1.8) return;

      wheelXRef.current += event.deltaX;

      window.clearTimeout(wheelResetRef.current);
      wheelResetRef.current = window.setTimeout(() => {
        wheelXRef.current = 0;
      }, TRACKPAD_RESET_MS);

      if (Math.abs(wheelXRef.current) >= TRACKPAD_THRESHOLD) {
        const direction = wheelXRef.current > 0 ? 1 : -1;
        wheelXRef.current = 0;
        navigateByDirection(direction);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('wheel', onWheel);
      window.clearTimeout(wheelResetRef.current);
      window.clearTimeout(cooldownRef.current);
    };
  }, [location.pathname, navigate]);

  return null;
};

export default SwipeNavigation;
